import { useCallback, useEffect, useState } from 'react';
import { fetchTransactions, fetchPlans, updatePlan } from '@api/backoffice';
import type { TransactionSortKey, SortDirection } from '@api/backoffice';
import useSnackbar from '@hooks/useSnackbar';
import { useTableSort } from '@hooks/useTableSort';
import ThSortable from '@components/Backoffice/ThSortable';
import ExportButton from '@components/Backoffice/ExportButton';
import { downloadBackofficeCsv, exportArrayToCsv } from '@utils/csvExport';
import {
  PageTitle,
  Card,
  CardHeader,
  Table,
  SearchBar,
  TabBar,
  Tab,
  PaginationBar,
  PaginationInfo,
  PaginationButton,
  PaginationActions,
  EmptyState,
  ActionButton,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  FormField,
  FormLabel,
  FormInput,
  ButtonRow,
  ToggleButton,
  SmallText,
  PositiveAmount,
  NegativeAmount,
  WarningBanner,
  InfoTooltip,
  TooltipText,
  TagList,
} from '@components/Backoffice/BackofficeLayout.styled';
import { formatTransactionType } from './formatters';
import type { Transaction, PageData, BillingPlan, EditPlanDialogProps } from './types';

const BackofficeBilling: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const [tab, setTab] = useState<'transactions' | 'plans'>('transactions');
  const [txData, setTxData] = useState<PageData<Transaction> | null>(null);
  const [orgFilter, setOrgFilter] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [editPlan, setEditPlan] = useState<BillingPlan | null>(null);
  const [txSortKey, setTxSortKey] = useState<TransactionSortKey>('createdAt');
  const [txSortDir, setTxSortDir] = useState<SortDirection>('desc');

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchTransactions({
        search: orgFilter || undefined,
        page,
        size: 20,
        sort: txSortKey,
        direction: txSortDir,
      });
      setTxData(result);
    } finally {
      setLoading(false);
    }
  }, [orgFilter, page, txSortKey, txSortDir]);

  const loadPlans = useCallback(async () => {
    const data = await fetchPlans();
    setPlans(data);
  }, []);

  useEffect(() => {
    if (tab === 'transactions') loadTransactions();
    else loadPlans();
  }, [tab, loadTransactions, loadPlans]);

  const txToggle = (key: string) => {
    const typedKey = key as TransactionSortKey;
    const newDir: SortDirection = typedKey === txSortKey ? (txSortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setTxSortKey(typedKey);
    setTxSortDir(newDir);
    setPage(0);
  };

  const {
    sorted: sortedPlans,
    sortKey: planSortKey,
    sortDirection: planSortDir,
    toggle: planToggle,
  } = useTableSort(plans, 'id');

  const txTh = (label: string, key: string) => (
    <ThSortable
      label={label}
      sortKey={key}
      activeKey={txSortKey}
      direction={txSortDir}
      onToggle={txToggle}
    />
  );

  const planTh = (label: string, key: string) => (
    <ThSortable
      label={label}
      sortKey={key}
      activeKey={planSortKey}
      direction={planSortDir}
      onToggle={planToggle}
    />
  );

  return (
    <>
      <PageTitle>Billing &amp; Credits</PageTitle>

      <TabBar>
        <Tab $active={tab === 'transactions'} onClick={() => setTab('transactions')}>
          Transactions
        </Tab>
        <Tab $active={tab === 'plans'} onClick={() => setTab('plans')}>
          Plans
        </Tab>
      </TabBar>

      {tab === 'transactions' && (
        <Card>
          <CardHeader>
            <SearchBar
              placeholder="Search by org ID, type, or description..."
              value={orgFilter}
              onChange={(e) => {
                setOrgFilter(e.target.value);
                setPage(0);
              }}
            />
            <ExportButton
              onExport={() =>
                downloadBackofficeCsv(
                  '/api/v1/backoffice/billing/transactions/export',
                  { search: orgFilter || '', sort: txSortKey, direction: txSortDir },
                  `transactions_${new Date().toISOString().slice(0, 10)}.csv`
                )
              }
            />
          </CardHeader>
          {loading && !txData ? (
            <EmptyState>Loading...</EmptyState>
          ) : txData?.content.length === 0 ? (
            <EmptyState>No transactions found</EmptyState>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    {txTh('Date', 'createdAt')}
                    {txTh('Type', 'type')}
                    {txTh('Amount', 'amount')}
                    {txTh('Balance After', 'balanceAfter')}
                    {txTh('Description', 'description')}
                    {txTh('Organization', 'organizationName')}
                    {txTh('User', 'userEmail')}
                    <th>Tools / Agents</th>
                  </tr>
                </thead>
                <tbody>
                  {(txData?.content ?? []).map((tx) => (
                    <tr key={tx.id}>
                      <td>{new Date(tx.createdAt).toLocaleString()}</td>
                      <td>{formatTransactionType(tx.type)}</td>
                      {tx.amount > 0 ? (
                        <PositiveAmount>+{tx.amount}</PositiveAmount>
                      ) : (
                        <NegativeAmount>{tx.amount}</NegativeAmount>
                      )}
                      <td>{tx.balanceAfter}</td>
                      <td>{tx.description || '—'}</td>
                      <td>
                        {tx.organizationName || '—'}
                        <br />
                        <SmallText>{tx.organizationId}</SmallText>
                      </td>
                      <td>{tx.userEmail || '—'}</td>
                      <TagList as="td">
                        {tx.toolNames?.length > 0
                          ? tx.toolNames.map((name, i) => <span key={`${name}-${i}`}>{name}</span>)
                          : '—'}
                      </TagList>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {txData && txData.totalPages > 1 && (
                <PaginationBar>
                  <PaginationInfo>
                    Page {txData.number + 1} of {txData.totalPages} ({txData.totalElements} total)
                  </PaginationInfo>
                  <PaginationActions>
                    <PaginationButton
                      $disabled={page === 0}
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      Previous
                    </PaginationButton>
                    <PaginationButton
                      $disabled={page >= txData.totalPages - 1}
                      disabled={page >= txData.totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </PaginationButton>
                  </PaginationActions>
                </PaginationBar>
              )}
            </>
          )}
        </Card>
      )}

      {tab === 'plans' && (
        <Card>
          <CardHeader>
            <span />
            <ExportButton
              onExport={async () =>
                exportArrayToCsv(
                  plans,
                  [
                    { header: 'ID', accessor: (p) => p.id },
                    { header: 'Monthly (cents)', accessor: (p) => String(p.monthlyPriceCents) },
                    { header: 'Yearly (cents)', accessor: (p) => String(p.yearlyPriceCents) },
                    { header: 'Active', accessor: (p) => String(p.active) },
                    { header: 'Features', accessor: (p) => p.features ?? '' },
                  ],
                  `plans_${new Date().toISOString().slice(0, 10)}.csv`
                )
              }
            />
          </CardHeader>
          <Table>
            <thead>
              <tr>
                {planTh('ID', 'id')}
                {planTh('Monthly (cents)', 'monthlyPriceCents')}
                {planTh('Yearly (cents)', 'yearlyPriceCents')}
                <th>
                  Active
                  <InfoTooltip>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                    </svg>
                    <TooltipText>
                      Inactive plans are hidden from new subscriptions but existing subscribers are unaffected.
                    </TooltipText>
                  </InfoTooltip>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedPlans.map((plan) => (
                <tr key={plan.id}>
                  <td>{plan.id}</td>
                  <td>{plan.monthlyPriceCents}</td>
                  <td>{plan.yearlyPriceCents}</td>
                  <td>
                    <ToggleButton $active={plan.active} onClick={async () => {
                      try {
                        await updatePlan(plan.id, { active: !plan.active });
                        showSuccess(`Plan ${plan.id} ${plan.active ? 'deactivated' : 'activated'}`);
                        loadPlans();
                      } catch (err) {
                        showError(err instanceof Error ? err.message : 'Failed to update plan');
                      }
                    }} />
                  </td>
                  <td>
                    <ActionButton $variant="ghost" onClick={() => setEditPlan(plan)}>
                      Edit
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {editPlan && (
        <EditPlanDialog
          plan={editPlan}
          onClose={() => setEditPlan(null)}
          onSave={() => {
            showSuccess(`Plan ${editPlan.id} updated`);
            loadPlans();
            setEditPlan(null);
          }}
          onError={(msg) => showError(msg)}
        />
      )}
    </>
  );
};

function EditPlanDialog({ plan, onClose, onSave, onError }: EditPlanDialogProps) {
  const [features, setFeatures] = useState(plan.features ?? '');
  const [saving, setSaving] = useState(false);

  const featuresChanged = features !== (plan.features ?? '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePlan(plan.id, { features });
      onSave();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Edit Plan: {plan.id}</DialogTitle>
        <FormField>
          <FormLabel>Monthly Price (cents)</FormLabel>
          <FormInput type="number" value={plan.monthlyPriceCents} disabled />
        </FormField>
        <FormField>
          <FormLabel>Yearly Price (cents)</FormLabel>
          <FormInput type="number" value={plan.yearlyPriceCents} disabled />
        </FormField>
        <FormField>
          <FormLabel>Features</FormLabel>
          <FormInput
            value={features}
            onChange={(e) => setFeatures(e.target.value)}
          />
        </FormField>
        {featuresChanged && (
          <WarningBanner>
            Features are managed by Liquibase migrations (dev team) and any changes made here may
            be overwritten in the next release. Only save if this is urgent.
          </WarningBanner>
        )}
        <ButtonRow>
          <ActionButton $variant="ghost" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton $variant="primary" onClick={handleSave} disabled={saving || !featuresChanged}>
            {saving ? 'Saving...' : 'Save'}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
}

export default BackofficeBilling;
